import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartLine, Brain, Target, FilePdf, Info } from '@phosphor-icons/react';
import { DataPanel } from './DataPanel';
import { generateMMIData } from './utils/mmiCalculations';

// Custom square shape for user prediction line
const SquareDot = (props) => {
  const { cx, cy, fill } = props;
  if (cx === undefined || cy === undefined) return null;
  const size = 8;
  return (
    <rect
      x={cx - size/2}
      y={cy - size/2}
      width={size}
      height={size}
      fill={fill || "#EC4899"}
      stroke="#EC4899"
      strokeWidth={2}
      transform={`rotate(45, ${cx}, ${cy})`}
    />
  );
};

export const MMIChart = ({ 
  estimatedMenopause, 
  prediction, 
  hormones, 
  lifestyle,
  predicting,
  onGetPrediction,
  onDownloadPDF
}) => {
  const referenceMMIData = generateMMIData(estimatedMenopause);
  
  return (
    <DataPanel title="My MenoPath Index (MMI) Timeline" icon={ChartLine} color="#EC4899" className="col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div>
          <p className="text-xs text-[#64748B]">Ovarian Reserve Index mapped against age-specific benchmarks</p>
        </div>
        <div className="flex gap-2">
          {prediction && (
            <Button
              onClick={onDownloadPDF}
              variant="outline"
              className="border-[#5B21B6] text-[#5B21B6] hover:bg-[#5B21B6]/10 rounded-lg"
              data-testid="download-pdf-btn"
            >
              <FilePdf className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
          <Button
            onClick={onGetPrediction}
            disabled={predicting || (!hormones && !lifestyle)}
            className="bg-gradient-to-r from-[#5B21B6] to-[#EC4899] hover:opacity-90 rounded-lg"
            data-testid="get-prediction-btn"
          >
            {predicting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Brain className="w-4 h-4 mr-2" />
                Run Analysis
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={referenceMMIData} margin={{ top: 10, right: 70, left: -10, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis 
              dataKey="age" 
              label={{ value: 'Age (years)', position: 'insideBottomRight', offset: 0, fontSize: 11, fill: '#64748B', dx: 35 }}
              tick={{ fill: '#64748B', fontSize: 11 }}
              tickMargin={8}
            />
            <YAxis 
              label={{ value: 'MMI', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#64748B' }}
              tick={{ fill: '#64748B', fontSize: 10 }}
              scale="log"
              domain={[1, 100]}
              ticks={[1, 2, 4, 8, 16, 32, 64, 100]}
              allowDataOverflow={true}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }}
              formatter={(value) => value?.toFixed(1)}
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            {/* Reference curves - All 6 lines with exact Excel data */}
            <Line type="monotone" dataKey="age48" name="Age 48" stroke="#DC2626" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="age49" name="Age 49" stroke="#92400E" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="age50" name="Age 50" stroke="#EAB308" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="age51" name="Age 51" stroke="#16A34A" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="age52" name="Age 52" stroke="#2563EB" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="age53" name="Age 53" stroke="#7C3AED" strokeWidth={2.5} dot={false} />
            
            {/* User's personalized prediction curve - dotted line with square dots */}
            <Line 
              type="monotone" 
              dataKey="yourPrediction" 
              name="Your Prediction"
              stroke="#6B7280" 
              strokeWidth={2} 
              strokeDasharray="5 5"
              dot={<SquareDot />}
              activeDot={{ fill: '#EC4899', strokeWidth: 2, r: 6 }}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {estimatedMenopause && (
        <div className="mt-4 p-4 bg-gradient-to-r from-[#EC4899]/5 to-[#8B5CF6]/5 rounded-xl border border-[#EC4899]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#EC4899] flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-[#64748B]">Predicted Menopause Onset</p>
              <p className="text-xl font-bold text-[#0F172A]">
                Age {estimatedMenopause.min} – {estimatedMenopause.max}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-[#64748B]">Confidence</p>
              <p className="text-lg font-semibold text-[#5B21B6]">{prediction?.confidence || 'Moderate'}</p>
            </div>
          </div>
        </div>
      )}

      {!estimatedMenopause && (
        <div className="mt-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
          <p className="text-sm text-[#64748B]">
            <Info className="w-4 h-4 inline mr-2" />
            {!hormones && !lifestyle 
              ? "Enter biomarker data and lifestyle factors to generate your personalized prediction."
              : !hormones 
                ? "Add your lab results to calculate your estimated menopause timeline."
                : "Complete your lifestyle assessment to refine your prediction."
            }
          </p>
        </div>
      )}
    </DataPanel>
  );
};
